// WO-GF-001 · DESIGN-SYSTEM card 86bb58y52 — THE ONE ICON RESOLUTION POINT.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────────
// THE INVESTIGATION THE FOUNDER ASKED FOR FIRST: "how does the current build serve icons —
// Strapi media or inline?" The answer is BOTH, in two disjoint places, and neither is manageable.
//
//   (1) CHROME / NAV icons are INLINE SVG STRINGS captured from the live mirror and committed into
//       generated TypeScript — `components/site/hubNavIcons.ts` opens with "GENERATED from the live
//       mirror capture … Do not hand-edit". `components/transport/booking/BookingIcons.tsx` is the
//       same shape. These are styled by the per-page inlined CSSOM and are pixel-gated.
//
//   (2) PERK / CATEGORY icons are SERVED FROM THE LEGACY STRAPI MEDIA BUCKET. Every perk and
//       supplier card in the captured mirror points at
//           https://snowbus-dev-strapi-image.s3.amazonaws.com/common/<file>.svg
//       1152 references across the mirror. THIS is the mechanism the founder remembers: the old
//       site managed icons through Strapi, and swapping the file in Strapi rotated the icon
//       everywhere at once, with no deploy.
//
// SO THE STRAPI-MANAGED PATH IS REAL — BUT IT BELONGS TO THE LEGACY STRAPI, NOT TO OURS.
// `snowclub-be` has NO icon/media field to wire this to. Checked, and this is the gap:
//   • `src/api/product-category/content-types/product-category/schema.json` — the obvious owner of a
//     category icon — declares exactly `name`, `description`, `slug`, `categoryType`. No media.
//   • `vendor` carries `logoUrl` (already routed at F177: `GET /api/vendors` is 403 to the public
//     role) — that is a SUPPLIER logo, not a category icon, and does not serve this.
//
// THE GAP, STATED SO IT CAN BE CLOSED LATER AND IS NOT QUIETLY WORKED AROUND:
//   to restore central rotation, `product-category` needs a media (or string-url) icon field, that
//   field needs a public-role find grant, and this module's map needs to read it. That is a
//   `snowclub-be` content-type change; this card is `needs-stack: root-fe`, so it is NOT done here.
//   ⚠ Per the card: "where it does not [exist], note the gap — do not invent a parallel icon store."
//   This file is therefore deliberately NOT a store. It is ONE resolution point with ONE map, whose
//   only job is to replace 1152 scattered hardcoded legacy URLs with a single place that a future
//   Strapi fetch overrides. Nothing else in the app may name an icon path.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// SUB-BRAND LOGOS — the card's row 3, and the finding is that it has NO IN-SCOPE TARGET here.
//
// The row reads: "serve sub-brand logos (snowclub, snowbus, reservewinter, snowgroup) from the staged
// SVGs WHERE CURRENTLY RASTER/AD-HOC." Both halves were checked against the tree rather than assumed,
// and both come back negative:
//
//  (1) THERE IS EXACTLY ONE RASTER SUB-BRAND TARGET, AND IT IS STILL OPEN — NOT DONE BY THIS CARD.
//      ⚠ AN EARLIER DRAFT OF THIS BLOCK CLAIMED THERE WAS NONE. That claim was FALSE and the way it
//      went wrong is worth keeping, because it is a search-scope error anyone repeats here: the
//      grep covered `apps/root/public/_mirror` and `apps/root/src`, but the captured pages live at
//      `apps/root/mirror/`. The adversarial review caught it.
//      THE REAL STATE: `_mirror/_next/static/media/footerLogo.50ca9bee.png` IS served, by
//      `mirror/contact-us.html` and `mirror/contact-us.mobile.html`, as
//      `<img alt="Title Logo" width="525" height="70" …>` — and the image is the SNOWGROUP WORDMARK,
//      i.e. one of the four sub-brands this row names, painted as a 525x70 raster on a live route.
//      The design system ships `assets/logos/snowgroup-logo.svg`, so a real replacement exists.
//      NOT SWAPPED HERE, and the reason is honesty about proof rather than difficulty: /contact-us
//      carries an at-rest pixel baseline, so the swap owes a recorded old->new baseline plus a gate
//      that fails when the SVG stops loading, and this run did not have the budget left to build and
//      break that gate. Shipping the swap unproven is the defect this lane exists to prevent.
//      Every OTHER sub-brand mark the site paints is already vector and therefore out of this row's
//      scope: `mainLogo.ec3de3aa.svg`, `headerLogoNew2.713156d5.svg`, `footerLogo.c84686f1.svg`,
//      `memberLogoImg.7ab26122.svg`.
//  (2) THE 614 RASTER `*logo*.png` REFERENCES ARE NOT OURS. Sampled across all 491 files that carry
//      them, every one is THIRD-PARTY artwork from the legacy Strapi media bucket —
//      `mountain_stowe_logo_*.png`, `activities_killington_logo_*.png`, `rental_mammoth_logo_*.png`:
//      mountains, operators and vendors. The design system ships no Stowe and no Killington mark, so
//      a swap here could only be a near-enough substitution, which is precisely the invention this
//      module exists to prevent. They stay exactly as they are.
//
// SO THE REMAINING QUESTION IS A BRAND RULING, NOT A BUILD GAP: whether the site's real captured
// SnowBus vector artwork should be replaced by the design system's own `snowbus-logo.svg` etc. That
// is a swap of real art for other real art across every page, it moves every at-rest pixel baseline
// in the suite, and it is a taste call — so it is ROUTED to the founder rather than guessed, on the
// same rule that left the hamburger's captured mirror icons alone.
//
// THE ASSETS ARE REAL AND THEIR CANONICAL PATHS ARE RECORDED so the next pass needs no discovery.
// `design-sync-staging/` never received them (its own README lists `logos/*.svg` under STILL OWED),
// but they DO exist in the Claude Design project `SnowGroup Design System`
// (b2ba4500-eb3d-431e-9e24-fcc4629e18aa), verified present by a live `list_files` read:
//   assets/logos/snowclub-logo.svg · snowbus-logo.svg · reservewinter-logo.svg · snowgroup-logo.svg
//   (+ snowgroup-logo-black/white.svg, snowclub-logomark.svg, snowgroup-logomark.svg, favicon.svg)
// They are NOT copied into the repo by this card: shipping unreferenced assets to satisfy a row whose
// only remaining blocker is a ruling would add dead weight and read as progress it is not.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Where the design-system SVGs are served from (copied verbatim from the Claude Design export). */
export const DS_ICON_BASE = "/design-system/icons";

/** The legacy Strapi media host that served the old, centrally-managed icon store. */
export const LEGACY_ICON_HOST = "snowbus-dev-strapi-image.s3.amazonaws.com";

/**
 * The 11 perks icons the design system ships. Named exactly as the files are named, because the
 * file name IS the icon's identity in the design system and re-labelling it here would be the
 * first step of a drift this module exists to prevent.
 */
export const PERK_ICON_NAMES = [
  "activities",
  "bars",
  "buy-sell",
  "dog-sleds",
  "insurance",
  "passes",
  "rentals",
  "restaurants",
  "snow-mobile",
  "snowshoes",
  "transport",
] as const;
export type PerkIconName = (typeof PERK_ICON_NAMES)[number];

/** The 14 standard (nav/menu) icons the design system ships. */
export const STANDARD_ICON_NAMES = [
  "activities",
  "buy-sell",
  "faq",
  "invite",
  "member-perks",
  "members",
  "mountains",
  "partnerships",
  "profile",
  "rentals",
  "saved-items",
  "store",
  "transport",
  "trips",
] as const;
export type StandardIconName = (typeof STANDARD_ICON_NAMES)[number];

export const perkIconUrl = (name: PerkIconName) => `${DS_ICON_BASE}/perks/${name}.svg`;
export const standardIconUrl = (name: StandardIconName) => `${DS_ICON_BASE}/standard/${name}.svg`;

/**
 * legacy Strapi `common/<file>` → design-system perks icon.
 *
 * MAPPED BY NAME, and the two that are not name-identical were read off the RENDERED CARD rather
 * than guessed from the filename:
 *   • `compass.svg` sits on the card whose own category text is "activity … activities"
 *     (Spicy Sports / Forged Axe Throwing / Ziptrek Ecotours) → `activities`
 *   • `boot.svg`    sits on the card whose own category text is "15% OFF Rentals"
 *     (Ski Butlers / Black Tie Ski Rentals) → `rentals`
 * The other three are unambiguous by name.
 *
 * DELIBERATELY ABSENT, because inventing a mapping is the defect this estate keeps paying for:
 *   • `snowbus-landing-main-image.svg` (392 refs) — a landing HERO image, not a category icon.
 *   • `JUNIOR/PREMIUM/PERFORMANCE/DEVELOPMENT.svg` — rental TIER badges. The design system ships no
 *     equivalent, so they keep their real artwork and are counted as unmapped, not substituted.
 * Anything not in this map is LEFT UNTOUCHED and reported — never swapped for a near-enough icon.
 *
 * ⚠ WHAT THE VISUAL READ-BACK FOUND, recorded because no DOM assertion can see it and the next reader
 * will otherwise call it our bug. On `/snowclub-perks` the two ACCOMMODATION cards (Pangea Pod Hotel,
 * Crystal Lodge) paint the `rentals` icon — a ski boot's replacement on a hotel. That is not the swap
 * mis-firing: read live, those cards' own `data-ds-icon-legacy-src` IS `boot.svg`, the same file the
 * genuine rental cards carry (measured 2026-07-29: 3×`compass.svg`, 5×`boot.svg` over the 8 swapped).
 * The LEGACY STRAPI DATA puts a boot on those hotels, so the swap is faithful 1:1 and merely inherits
 * the mismatch at higher fidelity. It is deliberately NOT patched here: the design system ships no
 * accommodation/lodging icon among its 11, so any fix from this file would be the invented near-enough
 * substitution this map exists to prevent. The real fix is the Strapi-managed category-icon field
 * described in the header — i.e. the same gap, showing itself as a visible symptom.
 */
export const LEGACY_COMMON_TO_PERK: Readonly<Record<string, PerkIconName>> = {
  "compass.svg": "activities",
  "boot.svg": "rentals",
  "rental.svg": "rentals",
  "snowmobile.svg": "snow-mobile",
  "snowshoe.svg": "snowshoes",
};

/**
 * menu-row LABEL → design-system STANDARD icon.
 *
 * THE ROW-2 RULE, and why this map is short on purpose. The card asks for the standard set to be
 * adopted "for nav/menu rows where the surface already renders an icon — 1:1 swap only where an icon
 * exists today; no new icon slots." The user dropdown is the one menu that qualifies without
 * reservation: `LoginPopup`'s own comment records that its glyphs are lucide components "chosen to the
 * mockup's drawings one for one" — i.e. an ad-hoc substitute set standing in for artwork we did not
 * have. We have it now, so the substitutes go and the real icons take their place.
 *
 * ONLY NAME-IDENTICAL MATCHES ARE LISTED. Every key below maps to the design-system file whose own
 * name is the row's own name, so no judgement is being exercised and nothing is being invented:
 *   My Trips → trips · Member Perks → member-perks · FAQ → faq · Profile → profile ·
 *   Saved Items → saved-items · Invite A Friend → invite · Agent & Partner → partnerships
 *
 * ⚠ "Buy & Sell" → `buy-sell` IS a name-identical pair and is STILL LEFT OUT, on evidence rather than
 * on taste. The glyphs paint as a CSS mask (see MenuGlyph), and a mask keyed on ALPHA cannot express a
 * KNOCK-OUT. `buy-sell.svg` draws the cart body and the tag body as `fill="#fff"` paths that sit AFTER
 * `</defs>` and INSIDE the viewBox — white holes that let the currentColor outlines read. Alpha 1 means
 * the mask makes them opaque, so the row would paint a solid filled cart instead of the outline icon:
 * the exact "looks deliberate, is wrong" blob this mechanism was supposed to avoid, and one no leg of
 * the gate can see (right URL, right box, HTTP 200). It keeps its lucide glyph until the icon is served
 * by a mechanism that honours the knock-out. SAME LATENT SHAPE, not currently mapped to any row:
 * `activities` · `rentals` · `store` · `transport`.
 *
 * DELIBERATELY ABSENT — these rows keep their existing glyph and are reported rather than
 * approximated, because picking a "close enough" icon is exactly the invention this card forbids:
 *   • "Login / Sign Up"  — the design system ships no sign-in icon.
 *   • "My Purchases"     — no purchases/orders icon. `store` and `trips` are different things.
 *   • "MySnowClub.Com"   — an outbound brand link, not one of the 14 standard glyphs.
 *
 * NOT EXTENDED TO THE HAMBURGER, deliberately, and the reason is the pixel gate: `SiteHeader`'s MENU
 * paints CAPTURED MIRROR ASSETS (`icon2.09beec18.svg` …), which are real live artwork styled by the
 * inlined per-page CSSOM and covered by the at-rest baselines. Swapping those is a re-shoot of
 * baselines this card did not authorise, and it is a swap of real art for other real art rather than
 * of a stand-in for the thing it stood in for. Reported, not done.
 */
export const MENU_LABEL_TO_STANDARD: Readonly<Record<string, StandardIconName>> = {
  "My Trips": "trips",
  "Member Perks": "member-perks",
  FAQ: "faq",
  Profile: "profile",
  "Saved Items": "saved-items",
  "Invite A Friend": "invite",
  "Agent & Partner": "partnerships",
};

/**
 * Resolve a menu row's label to its design-system icon, or null when we hold no name-identical match
 * (the caller then keeps whatever it renders today — it must never substitute a near-enough icon).
 */
export function resolveMenuIcon(label: string): { name: StandardIconName; url: string } | null {
  const name = MENU_LABEL_TO_STANDARD[label];
  return name ? { name, url: standardIconUrl(name) } : null;
}

/** True for any URL served by the legacy Strapi `common/` icon store. */
export function isLegacyCommonIcon(src: string): boolean {
  return src.includes(LEGACY_ICON_HOST) && src.includes("/common/");
}

/**
 * Resolve one legacy icon URL to its design-system replacement.
 * Returns null when the URL is not a legacy common icon, or is one we hold no honest mapping for.
 */
export function resolveLegacyIcon(src: string): { name: PerkIconName; url: string } | null {
  if (!isLegacyCommonIcon(src)) return null;
  const file = src.split("/").pop()?.split("?")[0];
  if (!file) return null;
  const name = LEGACY_COMMON_TO_PERK[file];
  if (!name) return null;
  return { name, url: perkIconUrl(name) };
}
